import { XMLSerializer, DOMParser } from '@xmldom/xmldom';
import interactionParserUtils from './InteractionParserUtils';
import xmlDomUtils from '../../../utils/XmlDomUtils';

const xmlSerializer = new XMLSerializer();

/**
 * Strips any XML declaration (<?xml ... ?>) from a string.
 * Newer @xmldom/xmldom versions throw if an XML declaration appears
 * anywhere other than the very start of the document.
 */
function stripXmlDeclaration(xmlString) {
    return xmlString ? xmlString.replace(/<\?xml[^?]*\?>/gi, '').trim() : '';
}

/**
 * Safely wraps content in a single root element, ensuring:
 * 1. No XML declaration survives into the wrapped string.
 * 2. The result always has exactly one root element (safe for strict xmldom).
 */
function safeWrap(htmlContent, wrapperTag = 'root') {
    const strippedContent = stripXmlDeclaration(htmlContent);
    return `<${wrapperTag}>${strippedContent}</${wrapperTag}>`;
}

/**
 * Removes the outer wrapper tag added by safeWrap after serialization.
 * The wrapper is purely structural (needed only to give xmldom a valid
 * single root) and must always be stripped from the final output.
 *
 * e.g. "<span><strong>A</strong></span>"       → "<strong>A</strong>"
 * e.g. "<span><span class='l'>X</span></span>" → "<span class='l'>X</span>"
 * e.g. "<span>text <i>word</i> more</span>"    → "text <i>word</i> more"
 */
function unwrapOuterTag(serializedString, wrapperTag) {
    const cleanedString = stripXmlDeclaration(serializedString).trim();
    const openingTag = `<${wrapperTag}`;
    const closingTag = `</${wrapperTag}>`;
    if (cleanedString.startsWith(openingTag) && cleanedString.endsWith(closingTag)) {
        const openTagEnd = cleanedString.indexOf('>');
        return cleanedString.slice(openTagEnd + 1, cleanedString.length - closingTag.length);
    }
    return cleanedString;
}

/**
 * Returns true when the string contains HTML markup that needs to be
 * re-parsed through xmldom (to process embedded <object> elements etc).
 * Covers closing tags, self-closing tags, <br>, and <img>.
 */
function isReparseNeeded(contentString) {
    if (!contentString || typeof contentString !== 'string') return false;
    const hasHtmlTags = /<\/|\/>|<br\b|<img\b/gi.test(contentString);
    return hasHtmlTags;
}

const matchParser = {
    parse(interactionNode, language, defaultLanguage, assessmentRunId) {
        const attributes = xmlDomUtils.getAttributes(interactionNode);
        const shuffle = attributes.shuffle === 'true';
        const randomise = attributes.randomise;
        const maxAssociations = attributes.maxAssociations;
        const matchSets = interactionParserUtils.parseListOfChildren(interactionNode, 'simpleMatchSet', language, defaultLanguage);
        const objects = interactionParserUtils.parseListOfChildren(interactionNode, 'object', language, defaultLanguage);
        const prompt = interactionParserUtils.getPrompt(interactionNode, language, defaultLanguage);

        const alternatives = matchSets.map((matchSet) => {
            const domParser = new DOMParser({
                // Suppress xmldom's verbose error/warning output.
                // We only propagate fatalErrors (genuine parse failures).
                errorHandler: {
                    warning: () => {},
                    error: () => {},
                    fatalError: (err) => { throw err; },
                },
            });

            // safeWrap strips any embedded <?xml?> declaration before wrapping,
            // which strict xmldom now rejects inside a child element.
            const wrappedMatchSet = safeWrap(matchSet.content);
            const choicesDoc = domParser.parseFromString(wrappedMatchSet, 'text/xml');
            const choices = choicesDoc.getElementsByTagName('simpleAssociableChoice');

            return Array.from(choices).map((choice) => {
                const choiceAttributes = xmlDomUtils.getAttributes(choice);
                let choiceContent = interactionParserUtils.getNodeContentAsString(choice, language, defaultLanguage);

                if (isReparseNeeded(choiceContent)) {
                    // Wrap in a single <span> root so xmldom always gets valid XML,
                    // regardless of whether content has one root element or many,
                    // or starts with a text node.
                    const wrappedChoiceContent = safeWrap(choiceContent, 'span');
                    const choiceDoc = domParser.parseFromString(wrappedChoiceContent, 'text/xml');

                    const objectElements = choiceDoc.getElementsByTagName('object');
                    Array.from(objectElements).forEach((objElm) => {
                        interactionParserUtils.replaceObjectWithImg(objElm, assessmentRunId);
                    });

                    choiceContent = xmlSerializer.serializeToString(choiceDoc);

                    // Remove formatting newlines XMLSerializer adds between text nodes and elements.
                    choiceContent = choiceContent.replace(/>[ \t]*\n[ \t]*</g, '><');

                    // Strip XML declaration that newer xmldom prepends to serializeToString output.
                    choiceContent = stripXmlDeclaration(choiceContent);

                    // Remove xmlns attributes added during serialization.
                    choiceContent = choiceContent.replace(/ xmlns="[^"]*"/g, '');

                    // Always unwrap the outer <span> — it was only needed to give xmldom
                    // a single valid root for parsing and must not appear in the final output.
                    choiceContent = unwrapOuterTag(choiceContent, 'span');
                }

                return Object.assign({}, choiceAttributes, { content: choiceContent });
            });
        });

        for (const object of objects) {
            object.data = interactionParserUtils.getMediaFileUrl(object, assessmentRunId);
        }

        return {
            alternatives,
            maxAssociations,
            objects,
            prompt,
            shuffle,
            randomise,
        };
    },
};

export default matchParser;
